import * as React from 'react';

import { Input } from '@independent-software/typeui/controls/Input';
import { Form } from '@independent-software/typeui/controls/Form';
import { Flex } from '@independent-software/typeui/controls/Flex';
import { Tabs } from '@independent-software/typeui/controls/Tabs';
import { Dropdown } from '@independent-software/typeui/controls/Dropdown';
import { Checkbox } from '@independent-software/typeui/controls/Checkbox';
import { Label } from '@independent-software/typeui/controls/Label';

import { Result, Sample, SampleFactory, Parameter, ParameterFactory } from '../../resource/';
import { Query } from '../../services/';

import { AuthContext } from '../../AuthContext';
import { Section } from '../../modules';
import { ValueDescription } from './ValueDescription';
import { ExtraValueDescription } from './ExtraValueDescription';

interface IProps {
  /** Initial form data. */
  data: Result;
  /** Called whenever form changes. */
  onChange: (data: Result, forceupdate: boolean) => void;
  /** Called whenever a field validates. Returns validation state for whole form. */
  onValidate: (valid: boolean) => void;
  /** 
   * Mark form as dirty. If dirty, it will show error messages for all fields,
   * even pristine fields.
   */  
  dirty?: boolean;
}

const ResultForm = (props: IProps) => {
  const auth = React.useContext(AuthContext);
  const [samples, setSamples] = React.useState([] as Sample[]);
  const [parameters, setParameters] = React.useState([] as Parameter[]);

  React.useEffect(() => {
    handleSampleSearch();
    handleParameterSearch();
  }, []);

  const handleSampleSearch = (q?: string) => {
    // Retrieve a list of samples:
    let query = new Query('date', 'asc');
    if (q) query.setFilter('q', 'like', q);
    SampleFactory.getSome(auth, 0, 16, query)
    .then(res => setSamples(res.items));
  }

  const handleParameterSearch = (q?: string) => {
    // Retrieve a list of parameters:
    let query = new Query('name', 'asc');
    if (q) query.setFilter('q', 'like', q);
    ParameterFactory.getSome(auth, 0, 16, query)
    .then(res => setParameters(res.items));
  }
  
  const renderSample = () => 
    <Form.Field
      required={{message: "Sample is required."}}
      width={1} label="Sample"
      name="sample" 
      value={props.data.sample}
      control={(<Dropdown onSearch={handleSampleSearch} fluid data={samples} placeholder="Sample" label={(item:Sample) => <><Label>{item.station.name}</Label> <Label>{item.campaign.name}</Label> <Label>{item.date}</Label></>}>
        <Dropdown.Column weight={1}>{(item:Sample) => item.station.name}</Dropdown.Column>
        <Dropdown.Column weight={2}>{(item:Sample) => item.campaign.name}</Dropdown.Column>
        <Dropdown.Column weight={1}>{(item:Sample) => item.date}</Dropdown.Column>
      </Dropdown>)}
      hint="Please select a sample."/>    

  const renderParameter = () => 
    <Form.Field
      required={{message: "Parameter is required."}}
      width={1} label="Parameter"
      name="parameter" 
      value={props.data.parameter}
      control={(<Dropdown onSearch={handleParameterSearch} fluid data={parameters} placeholder="Parameter" label={(item:Parameter) => item.name}>
        <Dropdown.Column weight={1}>{(item:Parameter) => item.name}</Dropdown.Column>
      </Dropdown>)}
      hint="Please select a parameter."/>    
  
  const renderValue = () =>
    <Form.Field 
      name="value" label="Value"
      value={props.data.value}
      control={(<Input type="text" placeholder="Value" fluid/>)}
      required={{message: "A value is required."}}
      isFloat={{message: "Value must be numeric."}}
      hint={(<React.Fragment>Please enter a result value.</React.Fragment>)}/>

  const renderDescription = () =>
    <Form.Field 
      name="description" label="Description"
      value={props.data.description}
      control={<Dropdown clearable placeholder="Description" fluid data={ValueDescription.getList()}
        label={(item) => ValueDescription.toName(item)}>
        <Dropdown.Column>{(item) => ValueDescription.toName(item)}</Dropdown.Column>
      </Dropdown>}
      hint={(<React.Fragment>Please select a result description.</React.Fragment>)}/>
        
  const renderExtraValue = () =>
    <Form.Field 
      name="extra_value" label="Extra value"
      value={props.data.extra_value}
      control={(<Input type="text" placeholder="Extra value" fluid/>)}
      isFloat={{message: "Extra value must be numeric."}}
      hint={(<React.Fragment>Please enter an extra value.</React.Fragment>)}/>

  const renderExtraDescription = () =>
    <Form.Field 
      name="extra_description" label="Extra description"
      value={props.data.extra_description}
      control={<Dropdown clearable placeholder="Extra description" fluid data={ExtraValueDescription.getList()}
        label={(item) => ExtraValueDescription.toName(item)}>
        <Dropdown.Column>{(item) => ExtraValueDescription.toName(item)}</Dropdown.Column>
      </Dropdown>}
      hint={(<React.Fragment>Please select an extra description.</React.Fragment>)}/>      

  const renderMinvalue = () =>
    <Form.Field 
      name="minvalue" label="Minimum value"
      value={props.data.minvalue}
      control={(<Input type="text" placeholder="Minimum value" fluid/>)}
      isFloat={{message: "Minimum value must be numeric."}}
      hint={(<React.Fragment>Please enter a minimum value.</React.Fragment>)}/>      

  const renderMaxvalue = () =>
    <Form.Field 
      name="maxvalue" label="Maximum value"
      value={props.data.maxvalue}
      control={(<Input type="text" placeholder="Maximum value" fluid/>)}
      isFloat={{message: "Maximum value must be numeric."}}
      hint={(<React.Fragment>Please enter a maximum value.</React.Fragment>)}/>            

  const renderReplicates = () =>
    <Form.Field 
      name="replicates" label="Replicates"
      value={props.data.replicates}
      control={(<Input type="text" placeholder="Replicates" fluid/>)}
      isInt={{message: "Replicates must be a whole number."}}
      hint={(<React.Fragment>Please enter a number of replicates.</React.Fragment>)}/>                  

  const renderDetectionLimit = () =>
    <Form.Field 
      name="detection_limit" 
      value={props.data.detection_limit == 1}
      control={(<Checkbox label="Detection limit" checked={props.data.detection_limit == 1}/>)}
      />  

  return (
    <Form
      data={props.data} dirty={props.dirty} onChange={props.onChange} onValidate={props.onValidate}>
      <Tabs underlined>
        <Tabs.Pane label="Data">
          <Section padded>
            <Flex.Columns count={2}>
              <>
                {renderSample()}
                {renderParameter()}
                {renderValue()}
                {renderDescription()}
                {renderDetectionLimit()}
              </>
              <>
              </>
            </Flex.Columns>
          </Section>
        </Tabs.Pane>
        <Tabs.Pane label="Additional data">
          <Section padded>
            <Flex.Columns count={2}>
              <>{renderExtraValue()}</>
              <></>
              <>{renderExtraDescription()}</>
              <>{renderMinvalue()}</>
              <>{renderMaxvalue()}</>
              <>{renderReplicates()}</>
            </Flex.Columns>
          </Section>
        </Tabs.Pane>
      </Tabs>
    </Form>
  )
}

export { ResultForm }
